/**
 * Le pincement de la carte (ticket 15 du chantier `telephone`, décision d'Igor du 2026-09-28) :
 * deux doigts zooment la carte, comme la molette sur ordinateur. Du calcul, sans navigateur :
 * `cameraCarte.ts` suit les doigts et appelle ceci.
 *
 * - Le facteur : l'écart des deux doigts maintenant, sur leur écart au départ. Écarter zoome,
 *   rapprocher dézoome.
 * - Le point immobile : le milieu des deux doigts. Le point de la carte qui était sous ce milieu
 *   au départ reste sous lui ; si les deux doigts glissent ensemble, la carte les suit.
 * - Les bornes sont celles de la molette (`plusPetite` et `ECHELLE_MAX`) : le pincement ne
 *   va jamais plus loin qu'elle.
 *
 * Aucune inertie : lâché, le zoom s'arrête là où sont les doigts.
 */
import type { Camera } from './borneDeLaCarte';

export interface Doigt {
  x: number;
  y: number;
}

export type DeuxDoigts = readonly [Doigt, Doigt];

export interface Bornes {
  min: number;
  max: number;
}

export function milieu([a, b]: DeuxDoigts): Doigt {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export function ecart([a, b]: DeuxDoigts): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Le facteur de zoom et le point immobile, de deux doigts au départ à deux doigts maintenant. */
export function pincement(depart: DeuxDoigts, maintenant: DeuxDoigts): { facteur: number; point: Doigt } {
  const avant = ecart(depart);
  // Deux doigts posés au même point : rien à mesurer, pas de zoom.
  const facteur = avant > 0 ? ecart(maintenant) / avant : 1;
  return { facteur, point: milieu(maintenant) };
}

/**
 * La caméra pendant un pincement, à partir de celle du départ. Doigts en pixels de la surface,
 * comme la caméra. L'échelle reste dans les bornes ; le point de la carte pincé au départ reste
 * sous le milieu des doigts.
 */
export function cameraPincee(depuis: Camera, depart: DeuxDoigts, maintenant: DeuxDoigts, bornes: Bornes): Camera {
  const { facteur, point } = pincement(depart, maintenant);
  const s = Math.min(Math.max(depuis.s * facteur, bornes.min), bornes.max);
  const pince = milieu(depart);
  // Le point de la carte sous le milieu au départ, dans le repère du titre.
  const monde = { x: (pince.x - depuis.x) / depuis.s, y: (pince.y - depuis.y) / depuis.s };
  return { s, x: point.x - monde.x * s, y: point.y - monde.y * s };
}
